import React, { useState } from 'react';

import copy from 'copy-to-clipboard';

import { IconButton } from '@atlaskit/button/new';
import CopyIcon from '@atlaskit/icon/core/copy';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { Box, Text } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useIntl } from 'src/hooks/intl';

import { messages } from './AiAssistant.i18n';
import { actionContainer } from './styles';

const COPY_ELAPSED_TIMEOUT = 2000;

type Props = {
  aiOutput: string;
};

type Feedback = 'up' | 'down';

type AiAssistantActionProps = {
  label: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  isSelected: boolean;
  icon: JSX.Element;
};
const AiAssistantAction = ({
  label,
  onClick,
  isSelected,
  icon,
}: AiAssistantActionProps): JSX.Element => {
  return (
    <Tooltip
      content={
        <Text size="small" color="color.text.inverse">
          {label}
        </Text>
      }
    >
      {tooltipProps => (
        <IconButton
          {...tooltipProps}
          icon={() => icon}
          label={label}
          spacing="compact"
          appearance="subtle"
          onClick={onClick}
          isSelected={isSelected}
        />
      )}
    </Tooltip>
  );
};

export const AiAssistantActions = ({ aiOutput }: Props): JSX.Element => {
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | ''>('');
  const [outputCopied, setOutputCopied] = useState<boolean>(false);
  const { trackAIFeedbackSubmit } = useAIEventsInstrumentation();
  const { formatMessage } = useIntl();

  const handleFeedbackClick = (
    e: React.MouseEvent<HTMLElement>,
    feedback: Feedback
  ) => {
    e?.preventDefault();
    if (feedback !== selectedFeedback) {
      trackAIFeedbackSubmit(feedback);
    }
    setSelectedFeedback(feedback);
  };

  const handleCopyOutputClick = (e: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    copy(aiOutput);
    setOutputCopied(true);
    setTimeout(() => setOutputCopied(false), COPY_ELAPSED_TIMEOUT);
  };

  return (
    <Box xcss={actionContainer}>
      <AiAssistantAction
        label={formatMessage(messages.helpful)}
        onClick={e => handleFeedbackClick(e, 'up')}
        isSelected={selectedFeedback === 'up'}
        icon={<ThumbsUpIcon label={formatMessage(messages.helpful)} />}
      />
      <AiAssistantAction
        label={formatMessage(messages.notHelpful)}
        onClick={e => handleFeedbackClick(e, 'down')}
        isSelected={selectedFeedback === 'down'}
        icon={<ThumbsDownIcon label={formatMessage(messages.notHelpful)} />}
      />
      <AiAssistantAction
        label={formatMessage(outputCopied ? messages.copied : messages.copy)}
        onClick={e => handleCopyOutputClick(e)}
        isSelected={false}
        icon={
          <CopyIcon
            label={formatMessage(
              outputCopied ? messages.copied : messages.copy
            )}
          />
        }
      />
    </Box>
  );
};
